import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fundingListsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import FundingCard from '../components/funding/FundingCard';
import FundingRow from '../components/funding/FundingRow';
import ExportListModal from '../components/funding/ExportListModal';

/* ─── View Toggle (matches FundingList) ───────────────────────────── */
const ViewToggle = ({ viewMode, setViewMode }) => {
  return (
    <div className="inline-flex items-center border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setViewMode('list')}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${
          viewMode === 'list'
            ? 'bg-gray-900 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        title="List view"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('card')}
        className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${
          viewMode === 'card'
            ? 'bg-gray-900 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        title="Card view"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
    </div>
  );
};

/* ─── Three-dot Menu ──────────────────────────────────────────────── */
const ListMenu = ({ list, onRename, onDuplicate, onDelete, onExport }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef} style={{ zIndex: open ? 100 : 'auto' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 16 16">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
          style={{ fontFamily: "'Poppins', system-ui, sans-serif", zIndex: 9999 }}
        >
          {!list.is_default && (
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); onRename(list); }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename
            </button>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDuplicate(list); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Duplicate
          </button>

          {!list.is_default && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(list); }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onExport(list); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Inline Rename Input ─────────────────────────────────────────── */
const InlineRenameInput = ({ value, onSave, onCancel }) => {
  const [name, setName] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      onCancel();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleSubmit();
        if (e.key === 'Escape') onCancel();
      }}
      className="font-semibold text-gray-900 bg-white border border-gray-300 rounded px-2 py-0.5 text-lg focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif", minWidth: '140px' }}
    />
  );
};

/* ─── Duplicate Modal ─────────────────────────────────────────────── */
const DuplicateModal = ({ list, onConfirm, onCancel }) => {
  const displayName = list.is_default ? 'Favourites' : list.name;
  const [name, setName] = useState(`${displayName} (copy)`);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
        style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Duplicate List</h3>
        <p className="text-sm text-gray-500 mb-4">
          This will create a copy of "{displayName}" with all {list.items_count ?? list.items?.length ?? 0} opportunities.
        </p>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim()); }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200 mb-4"
          placeholder="List name"
        />
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onConfirm(name.trim())}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#615DEC' }}
          >
            Create Copy
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Delete Confirmation Modal ───────────────────────────────────── */
const DeleteModal = ({ list, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onCancel}>
    <div
      className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete List</h3>
      <p className="text-sm text-gray-500 mb-4">
        Are you sure you want to delete "{list.name}"? This can't be undone.
      </p>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ─── List Accordion ──────────────────────────────────────────────── */
const ListAccordion = ({ list, viewMode, onRemoveItem, onRename, onDuplicate, onDelete, onExport }) => {
  const [expanded, setExpanded] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const itemCount = list.items_count ?? list.items?.length ?? 0;
  const displayName = list.is_default ? 'Favourites' : list.name;

  const handleRenameStart = () => setRenamingId(list.id);
  const handleRenameSave = (newName) => {
    setRenamingId(null);
    onRename(list.id, newName);
  };
  const handleRenameCancel = () => setRenamingId(null);

  return (
    <div className="border-2 border-gray-200 rounded-lg" style={{ overflow: 'visible' }}>
      <div
        className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors rounded-t-lg"
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className="flex items-center space-x-3">
          {renamingId === list.id && !list.is_default ? (
            <div onClick={(e) => e.stopPropagation()}>
              <InlineRenameInput
                value={list.name}
                onSave={handleRenameSave}
                onCancel={handleRenameCancel}
              />
            </div>
          ) : (
            <h3
              className="text-lg font-semibold text-gray-900"
              style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
            >
              {displayName}
            </h3>
          )}

          <span className="text-sm text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
            {itemCount}
          </span>

          {list.is_default && (
            <span
              className="px-2.5 py-0.5 text-xs font-medium rounded-full"
              style={{ backgroundColor: '#EDE9FE', color: '#615DEC' }}
            >
              Default
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div onClick={(e) => e.stopPropagation()}>
            <ListMenu
              list={list}
              onRename={() => handleRenameStart()}
              onDuplicate={() => onDuplicate(list)}
              onDelete={() => onDelete(list)}
              onExport={() => onExport(list)}
            />
          </div>
          <svg
            className={`h-5 w-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="bg-white border-t-2 border-gray-200">
          {itemCount === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-gray-400">No funding opportunities in this list yet.</p>
              <Link
                to="/funding"
                className="inline-block mt-3 text-sm font-medium hover:underline"
                style={{ color: '#615DEC' }}
              >
                Browse opportunities →
              </Link>
            </div>
          ) : viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {list.items.map(item => (
                <div key={item.id}>
                  <FundingCard
                    funding={{ ...item.funding_scheme, is_favourite: list.is_default }}
                    onUnsave={() => onRemoveItem(list.id, item.funding_scheme.id, list.is_default)}
                  />
                  {item.notes && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> {item.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {list.items.map(item => (
                <div key={item.id}>
                  <FundingRow
                    funding={{ ...item.funding_scheme, is_favourite: list.is_default }}
                    onUnsave={() => onRemoveItem(list.id, item.funding_scheme.id, list.is_default)}
                  />
                  {item.notes && (
                    <div className="mx-6 my-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> {item.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Create New List Button ──────────────────────────────────────── */
const CreateListButton = ({ onCreate }) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (creating) {
      inputRef.current?.focus();
    }
  }, [creating]);

  const handleCreate = () => {
    if (name.trim()) {
      onCreate(name.trim());
      setName('');
      setCreating(false);
    }
  };

  if (creating) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg px-6 py-4 flex items-center gap-3">
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
            if (e.key === 'Escape') { setCreating(false); setName(''); }
          }}
          placeholder="List name..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
          style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
        />
        <button
          onClick={handleCreate}
          disabled={!name.trim()}
          className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#615DEC' }}
        >
          Create
        </button>
        <button
          onClick={() => { setCreating(false); setName(''); }}
          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setCreating(true)}
      className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 flex items-center justify-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition-colors"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
      New List
    </button>
  );
};

/* ─── Main Page ───────────────────────────────────────────────────── */
const FundingListsPage = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exportTarget, setExportTarget] = useState(null);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const data = await fundingListsAPI.getLists();
      const sorted = [...(data.results || data)].sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setLists(sorted);
    } catch (err) {
      setError('Failed to load your lists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = (listId, fundingId, isDefault) => {
    setLists(prev => prev.map(list => {
      if (list.id !== listId) return list;
      return {
        ...list,
        items: list.items.filter(item => item.funding_scheme.id !== fundingId),
        items_count: (list.items_count ?? 1) - 1,
      };
    }));

    if (isDefault) {
      fundingListsAPI.unsaveOpportunity(fundingId).catch(console.error);
    } else {
      fundingListsAPI.removeFromList(fundingId, listId).catch(console.error);
    }
  };

  const handleRename = async (listId, newName) => {
    setLists(prev => prev.map(list =>
      list.id === listId ? { ...list, name: newName } : list
    ));
    try {
      await fundingListsAPI.renameList(listId, newName);
    } catch (err) {
      console.error('Failed to rename list:', err);
      fetchLists();
    }
  };

  const handleDuplicate = async (name) => {
    if (!duplicateTarget) return;
    try {
      await fundingListsAPI.duplicateList(duplicateTarget.id, name);
      setDuplicateTarget(null);
      fetchLists();
    } catch (err) {
      console.error('Failed to duplicate list:', err);
      setError('Failed to duplicate list');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setLists(prev => prev.filter(l => l.id !== targetId));
    setDeleteTarget(null);
    try {
      await fundingListsAPI.deleteList(targetId);
    } catch (err) {
      console.error('Failed to delete list:', err);
      fetchLists();
    }
  };

  const handleCreateList = async (name) => {
    try {
      await fundingListsAPI.createList(name);
      fetchLists();
    } catch (err) {
      console.error('Failed to create list:', err);
      setError('Failed to create list');
    }
  };

  if (loading) return <LoadingSpinner size="large" />;

  const defaultList = lists.find(l => l.is_default);
  const otherLists = lists.filter(l => !l.is_default);
  const totalItems = lists.reduce((sum, l) => sum + (l.items_count ?? l.items?.length ?? 0), 0);
  const isEmpty = totalItems === 0 && otherLists.length === 0;

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">My Lists</h1>
        </div>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {isEmpty ? (
        <div className="text-center py-16">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <h3 className="mt-4 text-base font-semibold text-gray-900">No saved opportunities yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start by browsing funding opportunities and adding them to your favourites.
          </p>
          <div className="mt-6">
            <Link
              to="/funding"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#615DEC' }}
            >
              Browse Opportunities
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {defaultList && (
            <ListAccordion
              list={defaultList}
              viewMode={viewMode}
              onRemoveItem={handleRemoveItem}
              onRename={handleRename}
              onDuplicate={setDuplicateTarget}
              onDelete={setDeleteTarget}
              onExport={setExportTarget}
            />
          )}

          {otherLists.map(list => (
            <ListAccordion
              key={list.id}
              list={list}
              viewMode={viewMode}
              onRemoveItem={handleRemoveItem}
              onRename={handleRename}
              onDuplicate={setDuplicateTarget}
              onDelete={setDeleteTarget}
              onExport={setExportTarget}
            />
          ))}

          <CreateListButton onCreate={handleCreateList} />
        </div>
      )}

      {duplicateTarget && (
        <DuplicateModal
          list={duplicateTarget}
          onConfirm={handleDuplicate}
          onCancel={() => setDuplicateTarget(null)}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          list={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {exportTarget && (
        <ExportListModal
          list={exportTarget}
          onCancel={() => setExportTarget(null)}
        />
      )}
    </div>
  );
};

export default FundingListsPage;